import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { CartItem, User, Language } from '../types'

interface AuthState {
  user: User | null
  token: string | null
  setAuth: (user: User, token: string) => void
  logout: () => void
}

interface LanguageState {
  language: Language
  setLanguage: (language: Language) => void
}

interface CartState {
  items: CartItem[]
  addItem: (item: CartItem) => void
  removeItem: (equipmentId: number) => void
  updateQuantity: (equipmentId: number, quantity: number) => void
  clearCart: () => void
  getTotalItems: () => number
}

// Auth store
export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      token: null,
      setAuth: (user, token) => {
        set({ user, token })
      },
      logout: () => {
        set({ user: null, token: null })
        localStorage.removeItem('broxel-auth')
        // Clear cart when user logs out
        useCartStore.getState().clearCart()
      },
    }),
    {
      name: 'broxel-auth',
    }
  )
)

// Language store
export const useLanguageStore = create<LanguageState>()(
  persist(
    (set) => ({
      language: 'en',
      setLanguage: (language) => set({ language }),
    }),
    {
      name: 'broxel-language',
    }
  )
)

// Cart store
export const useCartStore = create<CartState>()(
  persist(
    (set, get) => ({
      items: [],
      addItem: (newItem) =>
        set((state) => {
          const existingItemIndex = state.items.findIndex(
            (item) => item.equipment.id === newItem.equipment.id
          )
          
          if (existingItemIndex >= 0) {
            // Update existing item quantity
            const updatedItems = [...state.items]
            updatedItems[existingItemIndex].quantity += newItem.quantity
            return { items: updatedItems }
          } else {
            // Add new item
            return { items: [...state.items, newItem] }
          }
        }),
      removeItem: (equipmentId) =>
        set((state) => ({
          items: state.items.filter((item) => item.equipment.id !== equipmentId),
        })),
      updateQuantity: (equipmentId, quantity) =>
        set((state) => ({
          items: state.items.map((item) =>
            item.equipment.id === equipmentId
              ? { ...item, quantity: Math.max(0, quantity) }
              : item
          ).filter((item) => item.quantity > 0),
        })),
      clearCart: () => set({ items: [] }),
      getTotalItems: () => {
        return get().items.reduce((total, item) => total + item.quantity, 0)
      },
    }),
    {
      name: 'broxel-cart',
    }
  )
)
